import { HttpClient, HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from "@angular/material/button";
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { JwtInterceptor } from './authentications/JwtInterceptor.service';
import { FooterComponent } from './components/footer/footer.component';
import { ForgotPasswordPageComponent } from './components/forgot-password-page/forgot-password-page.component';
import { HeaderComponent } from './components/header/header.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { FooterService } from './services/footer.service';
import { HeaderWidgetComponent } from './shared/components/header-widget/header-widget';
import { SharedModule } from './shared/shared.module';
import { ApplicationInitializerFactory } from './application.initializer';
import { MetaService } from "./services/meta.service";
import { ResetPasswordPageComponent } from './components/reset-password-page/reset-password-page.component';
import { ActiveAccountComponent } from './components/active-account/active-account.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { ForbiddenComponent } from './components/forbidden/forbidden.component';
import { PlaybackErrorComponent } from './components/playback-error/playback-error.component';
import { LogoutComponent } from './components/logout/logout.component';
import { UserService } from './shared/services/user.service';
import { MAT_DATE_LOCALE } from "@angular/material/core";
import { AuthenticationService } from "./authentications/authentication.service";
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { ImpersonateComponent } from './impersonate/impersonate.component';
import { RumoService } from "./shared/services/rumo.service";
import { PageService } from './services/page.service';
import * as packageJson from './../../package.json';
import { NotificationsFacade } from './facades/notifications.facade';
import { UserBlockedGuard } from './guards/user-blocked-guard.service';
import { IsAuthenticatedGuard } from './guards/is-authenticated-guard';
import { CanCreateSubscriptionGuard } from './guards/can-create-subscription-guard.service';
import { NgcCookieConsentModule } from 'ngx-cookieconsent';
import { cookieConfig } from './app.cookie.config';
import { NavbarRhComponent } from './components/navbar-rh/navbar-rh.component';
import { SignInComponent } from './sign-in/sign-in.component';
import { CodeInputComponent } from './sign-in/components/code-input/code-input.component';
import { UserFeaturesBoxComponent } from "./components/header/user-features-box/user-features-box.component";
import { UmamiService } from "./services/umami.service";
import { HeaderLuxComponent } from "./components/header-lux/header-lux.component";
import { FooterLuxComponent } from "./components/footer-lux/footer-lux.component";
import { InvitationsService } from './rh/services/invitations.service';

// AoT requires an exported function for factories
export function httpLoaderFactory(http: HttpClient) {
  const { version } = packageJson;
  return new TranslateHttpLoader(http, './assets/i18n/', `.json`); //?version=${version}
}

registerLocaleData(localeFr);

const imports = [
  BrowserModule,
  BrowserAnimationsModule,
  HttpClientModule,
  FormsModule,
  MatButtonModule,
  TranslateModule.forRoot({
    defaultLanguage: 'en',
    loader: {
      provide: TranslateLoader,
      useFactory: httpLoaderFactory,
      deps: [HttpClient]
    }
  }),
  AppRoutingModule,
  SharedModule,
  NgcCookieConsentModule.forRoot(cookieConfig),
  UserFeaturesBoxComponent,
  // TODO: Remove when Lux Award is finished
  HeaderLuxComponent,
  FooterLuxComponent
];

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    HeaderWidgetComponent,
    NavbarComponent,
    NavbarRhComponent,
    FooterComponent,
    ForgotPasswordPageComponent,
    ResetPasswordPageComponent,
    ActiveAccountComponent,
    NotFoundComponent,
    ForbiddenComponent,
    PlaybackErrorComponent,
    LogoutComponent,
    ImpersonateComponent,
    SignInComponent,
    CodeInputComponent,
  ],
  imports: imports,
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: ApplicationInitializerFactory,
      deps: [TranslateService, Injector, AuthenticationService, UmamiService],
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    PageService,
    FooterService,
    UserService,
    MetaService,
    RumoService,
    AuthenticationService,
    NotificationsFacade,
    UserBlockedGuard,
    IsAuthenticatedGuard,
    CanCreateSubscriptionGuard,
    UmamiService,
    InvitationsService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
